import React from 'react';
import { Modal } from 'antd';
import request from './request';

// 附件上传的统一限制和错误说明。
// 服务器入口（nginx）限制单次请求体 50MB，更前面的 Cloudflare 限制 100MB：超过的文件会在到达应用之前就被拒绝，
// 所以必须在浏览器里提前拦截并提示，不能等上传失败。

export const MAX_UPLOAD_MB = 50;
export const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024;
/** 上传请求的超时时间：全局默认的 30 秒不够传较大的文件 */
export const UPLOAD_TIMEOUT_MS = 10 * 60 * 1000;
export const OVERSIZE_HINT = '较大的文件（尤其是视频）请上传到网盘，把链接写在备注里。';

type FileLike = { name?: string; uid?: string; size?: number; originFileObj?: { size?: number } };

export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '-';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))}KB`;
  const mb = bytes / (1024 * 1024);
  return `${mb >= 100 ? Math.round(mb) : Math.round(mb * 10) / 10}MB`;
}

export function fileSizeOf(file: FileLike | null | undefined): number {
  const size = file?.originFileObj?.size ?? file?.size;
  return typeof size === 'number' ? size : 0;
}

/** 是否是超过限制的"新选择的文件"（编辑时已保存在服务器上的旧附件不检查） */
export function isOversized(file: FileLike | null | undefined): boolean {
  if (!file) return false;
  if (String(file.uid || '').startsWith('existing-')) return false;
  return fileSizeOf(file) > MAX_UPLOAD_BYTES;
}

// ---- "文件太大"提示：页面中央的弹窗（比右上角的消息提示醒目，用户必须点"知道了"）----
// 一次选了多个超大文件时，各入口会连续调用多次：先攒起来，稍等一下合成一个弹窗，把它们都列出来。
let pendingOversize: Array<{ name: string; size: number }> = [];
let oversizeTimer: ReturnType<typeof setTimeout> | null = null;
let oversizeDialog: { destroy: () => void } | null = null;

export function showOversizeDialog(rejected: FileLike[]) {
  (rejected || []).forEach(file => pendingOversize.push({ name: String(file?.name || '未命名文件'), size: fileSizeOf(file) }));
  if (oversizeTimer) return;
  oversizeTimer = setTimeout(() => {
    oversizeTimer = null;
    const seen = new Set<string>();
    const items = pendingOversize.filter(item => {
      const key = `${item.name}|${item.size}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    pendingOversize = [];
    if (items.length === 0) return;
    if (oversizeDialog) oversizeDialog.destroy();
    oversizeDialog = Modal.warning({
      centered: true,
      width: 520,
      title: items.length === 1 ? '文件太大，无法上传' : `有 ${items.length} 个文件太大，无法上传`,
      content: React.createElement(
        'div',
        null,
        React.createElement(
          'div',
          { style: { maxHeight: 240, overflowY: 'auto', marginBottom: 10 } },
          items.map((item, index) => React.createElement(
            'div',
            { key: `${item.name}-${index}`, style: { marginBottom: 4 } },
            React.createElement('b', null, item.name),
            React.createElement('span', { style: { color: '#cf1322' } }, `（${formatFileSize(item.size)}）`),
          )),
        ),
        React.createElement('div', null, `单个文件不能超过 ${MAX_UPLOAD_MB}MB。${OVERSIZE_HINT}`),
      ),
      okText: '知道了',
      afterClose: () => { oversizeDialog = null; },
    });
  }, 60);
}

/** 选择单个文件时用：超过 50MB 就弹窗提示并返回 false（调用方据此拒绝上传） */
export function ensureUploadSize(file: FileLike | null | undefined): boolean {
  if (!isOversized(file)) return true;
  showOversizeDialog([file as FileLike]);
  return false;
}

/** 把文件列表按大小拆开：超过 50MB 的不接受 */
export function splitOversizedFiles<T extends FileLike>(files: T[]): { accepted: T[]; rejected: T[] } {
  const accepted: T[] = [];
  const rejected: T[] = [];
  (files || []).forEach(file => (isOversized(file) ? rejected : accepted).push(file));
  return { accepted, rejected };
}

export function oversizeMessage(rejected: FileLike[]): string {
  const list = rejected.map(file => `${String(file.name || '未命名文件')}（${formatFileSize(fileSizeOf(file))}）`).join('、');
  return `以下文件超过 ${MAX_UPLOAD_MB}MB，无法上传：${list}。${OVERSIZE_HINT}`;
}

/** 把上传失败的原因翻译成用户看得懂的话 */
export function describeUploadError(err: any): string {
  const response = err?.response;
  const status: number | undefined = response?.status;
  const data = response?.data;
  if (status === 413) return `文件太大，超过服务器允许的 ${MAX_UPLOAD_MB}MB`;
  if (err?.code === 'ECONNABORTED' || /timeout/i.test(String(err?.message || ''))) return '上传超时（网络较慢或文件较大）';
  if (!response) return '网络中断或被拦截（文件可能太大，或网络不稳定）';
  if (status === 401 || status === 403) return '没有权限，或登录已过期，请重新登录后再试';
  if (data && typeof data === 'object') {
    if (typeof data.detail === 'string' && data.detail) return data.detail;
    const flat = Object.values(data).flat().filter(item => typeof item === 'string') as string[];
    if (flat.length > 0) return flat.join('；');
  }
  if (status && status >= 500) return `服务器处理失败（HTTP ${status}）`;
  return status ? `上传失败（HTTP ${status}）` : String(err?.message || '未知错误');
}

export interface UploadFailure {
  name: string;
  reason: string;
}

/**
 * 上传一个指令附件。成功返回 null，失败返回 {文件名, 原因}（不抛错，由调用方汇总后统一提示）。
 * 使用 10 分钟超时，并回报上传进度（0-100）。
 */
export async function uploadInstructionAttachment(
  formData: FormData,
  fileName: string,
  onProgress?: (percent: number) => void,
): Promise<UploadFailure | null> {
  try {
    await request.post('/api/v1/instruction/attachments/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: UPLOAD_TIMEOUT_MS,
      onUploadProgress: (event: any) => {
        if (!onProgress) return;
        const total = Number(event?.total || 0);
        if (total > 0) onProgress(Math.min(100, Math.round((Number(event.loaded || 0) * 100) / total)));
      },
    });
    return null;
  } catch (err: any) {
    return { name: fileName, reason: describeUploadError(err) };
  }
}
